import { Fragment, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XIcon, ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useSite } from '../content';
import type { SelectWorkProject } from '../content/types';

const easeOut = [0.22, 1, 0.36, 1] as const;

interface SelectWorkProps {
  onOpenContact: (inquiryType?: string) => void;
}

export function SelectWork({ onOpenContact }: SelectWorkProps) {
  const { selectWork } = useSite();
  const prefersReducedMotion = useReducedMotion();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<SelectWorkProject | null>(null);

  if (!selectWork.projects.length) return null;

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.clientWidth / 3;
    el.scrollBy({
      left: dir * cardWidth,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <section
      id="selected-work"
      aria-labelledby="selected-work-heading"
      className="relative w-full bg-slate-dark py-28 md:py-36"
    >
      <div className="px-6 md:px-12 lg:px-16 mb-12 md:mb-16 flex items-end justify-between gap-8">
        <div className="max-w-2xl">
          {selectWork.eyebrow ? (
            <p className="text-xs uppercase tracking-[0.25em] text-bronze mb-5">
              {selectWork.eyebrow}
            </p>
          ) : null}
          <h2
            id="selected-work-heading"
            className="font-serif font-light text-textlight text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            {selectWork.heading}
          </h2>
          {selectWork.intro ? (
            <p className="mt-6 text-textlight/60 text-base md:text-lg leading-relaxed max-w-lg">
              {selectWork.intro}
            </p>
          ) : null}
        </div>

        <div className="hidden md:flex items-center gap-px shrink-0">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Previous project"
            className="w-12 h-12 border border-textlight/20 text-textlight/70 hover:text-textlight hover:border-bronze hover:bg-bronze/10 transition-colors flex items-center justify-center"
          >
            <ArrowLeftIcon size={18} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Next project"
            className="w-12 h-12 border border-textlight/20 text-textlight/70 hover:text-textlight hover:border-bronze hover:bg-bronze/10 transition-colors flex items-center justify-center"
          >
            <ArrowRightIcon size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="select-work-scroller flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 md:px-12 lg:px-16 pb-4"
        style={{ scrollbarWidth: 'none' }}
      >
        <style>{`.select-work-scroller::-webkit-scrollbar { display: none; }`}</style>
        {selectWork.projects.map((project, idx) => (
          <motion.button
            type="button"
            key={project.id || project.title || idx}
            onClick={() => setActive(project)}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{
              duration: prefersReducedMotion ? 0 : 0.7,
              delay: prefersReducedMotion ? 0 : idx * 0.08,
              ease: easeOut,
            }}
            aria-label={`View case study: ${project.title}`}
            className="group relative shrink-0 snap-start text-left w-[78vw] sm:w-[55vw] md:w-[calc((100%-3rem)/3)] aspect-[3/4] overflow-hidden focus:outline-none focus-visible:ring-1 focus-visible:ring-bronze"
          >
            {project.coverImageUrl ? (
              <img
                src={project.coverImageUrl}
                alt={project.coverImageAlt || project.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
              />
            ) : null}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"
            />
            <div aria-hidden="true" className="absolute inset-0 border border-textlight/5 pointer-events-none" />

            <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
              {project.tags.length ? (
                <div className="hidden md:flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-4">
                  {project.tags.map((tag, i) => (
                    <Fragment key={tag}>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-bronze-light">
                        {tag}
                      </span>
                      {i < project.tags.length - 1 ? (
                        <span aria-hidden="true" className="text-bronze/40 text-[10px]">
                          ·
                        </span>
                      ) : null}
                    </Fragment>
                  ))}
                </div>
              ) : null}
              <h3 className="font-serif font-light text-textlight text-xl md:text-2xl leading-tight tracking-tight mb-3">
                {project.title}
              </h3>
              <p className="text-textlight/65 text-sm leading-relaxed line-clamp-2">
                {project.descriptor}
              </p>

              <div className="mt-5 pt-4 border-t border-textlight/15 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-textlight/45">
                <span>{project.location}</span>
                <span className="text-bronze opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  View case study →
                </span>
              </div>
            </div>
          </motion.button>
        ))}

        <div className="shrink-0 w-px md:w-8" aria-hidden="true" />
      </div>

      <AnimatePresence>
        {active ? (
          <CaseStudy
            project={active}
            onClose={() => setActive(null)}
            onOpenContact={(inquiryType) => {
              setActive(null);
              onOpenContact(inquiryType);
            }}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
}

interface CaseStudyProps {
  project: SelectWorkProject;
  onClose: () => void;
  onOpenContact: (inquiryType?: string) => void;
}

function CaseStudy({ project, onClose, onOpenContact }: CaseStudyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useFocusTrap(containerRef, true, onClose);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <motion.div
      ref={containerRef}
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={prefersReducedMotion ? undefined : { opacity: 0 }}
      transition={{ duration: prefersReducedMotion ? 0 : 0.4 }}
      className="fixed inset-0 z-[100] bg-slate-dark/95 backdrop-blur-sm overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-study-title"
    >
      <div className="sticky top-0 z-10 flex justify-end px-6 md:px-12 pt-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close case study"
          className="w-12 h-12 border border-textlight/20 bg-slate-dark/80 text-textlight/80 hover:text-textlight hover:border-bronze hover:bg-bronze/10 transition-colors flex items-center justify-center"
        >
          <XIcon size={20} strokeWidth={1.5} />
        </button>
      </div>

      <motion.article
        initial={prefersReducedMotion ? false : { y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={prefersReducedMotion ? undefined : { y: 12, opacity: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.55, ease: easeOut, delay: 0.05 }}
        className="max-w-5xl mx-auto px-6 md:px-12 pb-32 -mt-12"
      >
        <p className="text-xs uppercase tracking-[0.25em] text-bronze mb-6">Case Study</p>

        <h2
          id="case-study-title"
          className="font-serif font-light text-textlight text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
        >
          {project.title}
        </h2>

        <div className="mt-8 pt-6 border-t border-textlight/15 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.22em] text-textlight/55">
          {project.location ? (
            <div>
              <span className="text-textlight/35 mr-3">Location</span>
              {project.location}
            </div>
          ) : null}
          {project.year ? (
            <div>
              <span className="text-textlight/35 mr-3">Year</span>
              {project.year}
            </div>
          ) : null}
          {project.tags.length ? (
            <div>
              <span className="text-textlight/35 mr-3">Scope</span>
              {project.tags.join(' · ')}
            </div>
          ) : null}
        </div>

        {project.heroImageUrl ? (
          <div className="mt-12 aspect-[16/9] overflow-hidden">
            <img
              src={project.heroImageUrl}
              alt={project.heroImageAlt || project.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : null}

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-12">
          {project.context ? (
            <>
              <div className="md:col-span-4">
                <p className="text-[11px] uppercase tracking-[0.25em] text-bronze">Context</p>
              </div>
              <div className="md:col-span-8">
                <p className="font-serif font-light text-textlight text-xl md:text-2xl leading-[1.55] tracking-tight">
                  {project.context}
                </p>
              </div>
            </>
          ) : null}

          {project.approach.length ? (
            <>
              <div className="md:col-span-4">
                <p className="text-[11px] uppercase tracking-[0.25em] text-bronze">Approach</p>
              </div>
              <div className="md:col-span-8">
                <ul className="divide-y divide-textlight/10 border-t border-b border-textlight/10">
                  {project.approach.map((item, i) => (
                    <li key={item} className="py-4 flex items-baseline gap-6 text-textlight/85">
                      <span className="text-xs text-bronze/70 tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-base md:text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : null}

          {project.outcome ? (
            <>
              <div className="md:col-span-4">
                <p className="text-[11px] uppercase tracking-[0.25em] text-bronze">Outcome</p>
              </div>
              <div className="md:col-span-8">
                <p className="font-serif font-light italic text-textlight text-xl md:text-2xl leading-[1.55] tracking-tight">
                  {project.outcome}
                </p>
              </div>
            </>
          ) : null}
        </div>

        <div className="mt-16 pt-8 border-t border-textlight/15 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => onOpenContact()}
            className="inline-flex items-center gap-2 font-sans text-base text-white hover:text-bronze underline underline-offset-4 decoration-bronze decoration-2 hover:decoration-bronze/70 transition-colors min-h-[44px]"
          >
            Get in touch about a project like this
            <span aria-hidden="true">→</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] uppercase tracking-[0.22em] text-textlight/70 hover:text-bronze transition-colors min-h-[44px]"
          >
            Close ✕
          </button>
        </div>
      </motion.article>
    </motion.div>
  );
}
